import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { apiService } from '../apiService';
import { Invoice, Product, Unit, Packaging, VATRate, Inventory, Transaction, 
        TransactionType, Farm, UnitType, ProductCategory, ProductIngredient, ProductUsage, 
        CultureStage,
        Culture,
        SplitFertigation,
        InvoiceProduct,
        SprayCampaign,
        SprayProduct,
        Pest,
        SprayExecuted,
        BlockCulture,
        Block,
        Tree} from "../../types";
import { Formulation, Category, Ingredient } from '../../types';
import { UsageMode } from '../../types/usageMode';
import { NutritionApplications, NutritionEntity } from '../../types/nutrition';
import { useSessionStore } from './sessionStore';
import { Variety } from '../../types/variety';
import { FruitSizeMeasurement } from '../../types/fruitsize';
import { formatDateYYYYMMDD } from '../../helpers/utils';
import { FruitSizeMeasurementStats } from '../../types/fruitfizemeasurementstats';

const DEFAULT_STALE_TIME = {
  VERY_LONG: 60*60*1000,
  LONG: 600*1000,
  MEDIUM: 300*1000,
  SHORT: 60*1000,
  PING: 10*1000
} as const;

export const QUERY_KEYS = {
  CATEGORIES: 'categories',
  CONCENTRATION_UNITS: 'concentrationUnits',
  CULTURE_STAGES: 'cultureStages',
  CULTURES: 'cultures',
  FARM_DETAILS: 'farmDetails',
  FARMS: 'farms',
  FRUIT_SIZE_MEASUREMENTS: 'fruitSizeMeasurements',
  FRUIT_SIZE_MEASUREMENT_STATS: 'fruitSizeMeasurementStats',
  INGREDIENTS: 'ingredients',
  INVENTORY: 'inventory',
  INVENTORY_PRODUCT: 'inventoryProduct',
  INVENTORY_TRANSACTION_TYPES: 'inventoryTransactionTypes',
  INVOICE_PRODUCTS: 'invoiceProducts',
  INVOICES: 'invoices',
  NUTRITION: 'nutrition',
  NUTRITION_APPLICATIONS: 'nutritionApplications',
  NUTRITION_ENTITIES: 'nutritionEntities',
  NUTRITION_PRODUCTS: 'nutritionProducts',
  PACKAGING: 'packaging',
  PESTS: 'pests',
  PING_SERVER: 'pingServer',
  PRODUCT_CATEGORIES: 'productCategories',
  PRODUCT_FORMULATIONS: 'formulations',
  PRODUCT_INGREDIENTS: 'productIngredients',
  PRODUCT_TRANSACTIONS: 'productTransactions',
  PRODUCT_USAGES: 'productUsages',
  PRODUCTS: 'products',
  SPLIT_FERTIGATIONS: 'splitFertigations',
  SPRAY_CAMPAIGN_PRODUCTS: 'sprayCampaignProducts',
  SPRAY_EXECUTED: 'sprayExecuted',
  SPRAY_PLANNED: 'sprayPlanned',
  SPRAY_PRODUCTS: 'sprayProducts',
  UNIT_LIST: 'unitList',
  UNITS: 'units',
  USAGE_MODES: 'usageModes',
  VAT_RATES: 'vatRates',  
  FARM_BLOCKS_CULTURES: 'farmBlocksCultures',
  TREES: 'trees',
  BLOCKS: 'blocks',
  VARIETIES: 'varieties',
} as const;

export const useQueryRetrieveFarms = () => {
  return useQuery<Farm[], Error>({
    queryKey: [QUERY_KEYS.FARMS],
    queryFn: retrieveFarms,
    staleTime: DEFAULT_STALE_TIME.LONG
  })
}

export const useQueryRetrieveFarmDetails = () => {
  const farmID = useSessionStore().currentFarm.farmID;
  return useQuery<Farm, Error>({
    queryKey: [QUERY_KEYS.FARM_DETAILS, farmID],
    queryFn: () => retrieveFarmDetails(farmID),
    staleTime: DEFAULT_STALE_TIME.LONG
  })
}

export const useQueryRetrieveBlocksCultures = () => {
  const farmID = useSessionStore().currentFarm.farmID;
  return useQuery<BlockCulture[], Error>({
    queryKey: [QUERY_KEYS.FARM_BLOCKS_CULTURES, farmID],
    queryFn: () => retrieveBlocksCultures(farmID),
    staleTime: DEFAULT_STALE_TIME.LONG
  })
}

export const useQueryRetrieveTrees = () => {
  const farmID = useSessionStore().currentFarm.farmID;
  return useQuery<Tree[], Error>({
    queryKey: [QUERY_KEYS.TREES, farmID],
    queryFn: () => retrieveTrees(farmID),
    staleTime: DEFAULT_STALE_TIME.LONG
  })
}


export const useQueryRetrieveInvoices = () => {
    const { selectedYear } = useSessionStore();
    const farmID = useSessionStore().currentFarm.farmID;
  
    return useQuery<Invoice[], Error>({
          queryKey: [QUERY_KEYS.INVOICES, farmID, selectedYear],
          queryFn: () => retrieveInvoices(farmID, selectedYear),
          enabled: (farmID != null) && (farmID.length > 0),
          staleTime: DEFAULT_STALE_TIME.VERY_LONG,
          throwOnError: false
        })
  }
  
export const useQueryRetrieveProducts = () => {
  const farmID = useSessionStore().currentFarm.farmID;
    return useQuery<Product[], Error>({
          queryKey: [QUERY_KEYS.PRODUCTS, farmID],
          queryFn: () => retrieveProducts(farmID),
          enabled: (farmID != null) && (farmID.length > 0),
          staleTime: DEFAULT_STALE_TIME.VERY_LONG,
          throwOnError: false
        })
  }

  export const useQueryRetrieveInvoiceProducts = (productID: string) => {
    const farmID = useSessionStore().currentFarm.farmID;
    return useQuery<InvoiceProduct[], Error>({
          queryKey: [QUERY_KEYS.INVOICE_PRODUCTS, farmID, productID],
          queryFn: () => retrieveInvoiceProducts(farmID, productID),
          enabled: (farmID != null) && (farmID.length > 0),
          staleTime: DEFAULT_STALE_TIME.SHORT,
          throwOnError: false
        })
  }

  export const useQueryRetrieveProductCategories = (productID: string) => {
    return useQuery<ProductCategory[], Error>({
          queryKey: [QUERY_KEYS.PRODUCT_CATEGORIES, productID],
          queryFn: () => retrieveProductCategories(productID),
          enabled: (productID.length > 0),
          staleTime: DEFAULT_STALE_TIME.SHORT,
          throwOnError: false
        })
  }


  export const useQueryRetrieveProductIngredients = (productID: string) => {
    return useQuery<ProductIngredient[], Error>({
          queryKey: [QUERY_KEYS.PRODUCT_INGREDIENTS, productID],
          queryFn: () => retrieveProductIngredients(productID),
          enabled: ((productID?.length || 0) > 0),
          staleTime: DEFAULT_STALE_TIME.SHORT,
          throwOnError: false
        })
  }


  export const useQueryRetrieveProductUsages = (productID: string) => {
    return useQuery<ProductUsage[], Error>({
          queryKey: [QUERY_KEYS.PRODUCT_USAGES, productID],
          queryFn: () => retrieveProductUsages(productID),
          enabled: ((productID?.length || 0) > 0),
          staleTime: DEFAULT_STALE_TIME.VERY_LONG,
          throwOnError: false
        })
  }
  
  export const useQueryRetrieveVATRates = () => {
    const farmID = useSessionStore().currentFarm.farmID;
    return useQuery<VATRate[], Error>({
          queryKey: [QUERY_KEYS.VAT_RATES, farmID],
          queryFn: () => retrieveVATRates(farmID),
          enabled: (farmID != null) && (farmID.length > 0),
          staleTime: DEFAULT_STALE_TIME.VERY_LONG,
          throwOnError: false
        })
  }

  export const useQueryRetrieveInventory = () => {
    const selectedYear = useSessionStore(state => state.selectedYear);
    const farmID = useSessionStore().currentFarm.farmID;
    return useQuery<Inventory, Error>({
          queryKey: [QUERY_KEYS.INVENTORY, farmID, selectedYear],
          queryFn: () => retrieveInventory(farmID, selectedYear),
          enabled: (farmID != null) && (farmID.length > 0),
          staleTime: DEFAULT_STALE_TIME.SHORT,
          throwOnError: false
        })
  }

  export const useQueryRetrieveInventoryProduct = (productID: string) => {
    const selectedYear = useSessionStore(state => state.selectedYear);
    const farmID = useSessionStore().currentFarm.farmID;
    return useQuery<Inventory, Error>({
          queryKey: [QUERY_KEYS.INVENTORY_PRODUCT, farmID, productID, selectedYear],
          queryFn: () => retrieveInventoryProduct(farmID, productID, selectedYear),
          enabled: (farmID != null) && (farmID.length > 0) && !!productID && (productID.length > 0),
          staleTime: DEFAULT_STALE_TIME.SHORT,
          throwOnError: false
        })
  }

  export const useQueryRetrieveProductTransactions = (productID: string) => {
    const selectedYear = useSessionStore(state => state.selectedYear);
    const farmID = useSessionStore().currentFarm.farmID;
    return useQuery<Transaction[], Error>({
      queryKey: [QUERY_KEYS.PRODUCT_TRANSACTIONS, farmID, productID, selectedYear],
      queryFn: () => retrieveProductTransactions(farmID, productID, selectedYear),
      enabled: (productID.length > 0) && (farmID != null) && (farmID.length > 0),
      staleTime: DEFAULT_STALE_TIME.SHORT,
      throwOnError: false
    })
}
export const useQueryRetrieveTransactions = () => {
  const selectedYear = useSessionStore(state => state.selectedYear);
  const farmID = useSessionStore().currentFarm.farmID;
  return useQuery<Transaction[], Error>({
    queryKey: [QUERY_KEYS.PRODUCT_TRANSACTIONS, farmID,  selectedYear],
    queryFn: () => retrieveTransactions(farmID, selectedYear),
    enabled: (farmID != null) && (farmID.length > 0),
    staleTime: DEFAULT_STALE_TIME.SHORT,
    throwOnError: false
  })
}


export const useQueryRetrieveInventoryTransactionTypes = () => {
  return useQuery<TransactionType[], Error>({
    queryKey: [QUERY_KEYS.INVENTORY_TRANSACTION_TYPES],
    queryFn: () => retrieveInventoryTransactionTypes(),
    enabled: true,
    staleTime: DEFAULT_STALE_TIME.VERY_LONG,
    throwOnError: false
  })
}

export const useQueryRetrieveFormulations = () => {
  return useQuery<Formulation[], Error>({
    queryKey: [QUERY_KEYS.PRODUCT_FORMULATIONS],
    queryFn: () => retrieveFormulations(),
    enabled: true,
    staleTime: DEFAULT_STALE_TIME.VERY_LONG,
    throwOnError: false
  })
}

export const useQueryRetrieveConcentrationUnits = () => {
  return useQuery<Unit[], Error>({
        queryKey: [QUERY_KEYS.CONCENTRATION_UNITS],
        queryFn: () => retrieveUnits (false, true),
        staleTime: DEFAULT_STALE_TIME.VERY_LONG,
        throwOnError: false
      })
}

export const useQueryRetrieveUnitList = (unitListType: UnitType = UnitType.Any) => {
  return useQuery<Unit[], Error>({
        queryKey: [QUERY_KEYS.UNIT_LIST, unitListType],
        queryFn: () => retrieveUnitList (unitListType),
        staleTime: DEFAULT_STALE_TIME.VERY_LONG,
        throwOnError: false
      })
}


export const useQueryRetrieveUnits = () => {
    return useQuery<Unit[], Error>({
          queryKey: [QUERY_KEYS.UNITS],
          queryFn: () => retrieveUnits (false, false),
          staleTime: DEFAULT_STALE_TIME.VERY_LONG,
          throwOnError: false
        })
  }


  export const useQueryRetrieveIngredients = () => {
    return useQuery<Ingredient[], Error>({
          queryKey: [QUERY_KEYS.INGREDIENTS],
          queryFn: retrieveIngredients,
          staleTime: DEFAULT_STALE_TIME.VERY_LONG,
          throwOnError: false
        })
  }

  export const useQueryRetrievePackaging = () => {
    return useQuery<Packaging[], Error>({
          queryKey: [QUERY_KEYS.PACKAGING],
          queryFn: retrievePackagings,
          staleTime: DEFAULT_STALE_TIME.VERY_LONG,
          throwOnError: false
        })
  }  


  export const useQueryRetrieveCategories = () => {
    return useQuery<Category[], Error>({
          queryKey: [QUERY_KEYS.CATEGORIES],
          queryFn: retrieveCategories,
          staleTime: DEFAULT_STALE_TIME.VERY_LONG,
          throwOnError: false
        })
  }

  export const useQueryRetrieveUsageModes = () => {
    return useQuery<UsageMode[], Error>({
          queryKey: [QUERY_KEYS.USAGE_MODES],
          queryFn: retrieveUsageModes,
          staleTime: DEFAULT_STALE_TIME.VERY_LONG,
          throwOnError: false
        })
  }

    export const useQueryRetrieveCultures = () => {
      return useQuery<Culture[], Error>({
            queryKey: [QUERY_KEYS.CULTURES],
            queryFn: retrieveCultures,
            staleTime: DEFAULT_STALE_TIME.VERY_LONG,
            throwOnError: false
          })
    }

    export const useQueryRetrieveBlocks = () => {
      const farmID = useSessionStore().currentFarm.farmID;
      return useQuery<Block[], Error>({
        queryKey: [QUERY_KEYS.BLOCKS, farmID],
        queryFn: () => retrieveBlocks(farmID),
        staleTime: DEFAULT_STALE_TIME.VERY_LONG,
        throwOnError: false
      })
    }

    export const useQueryRetrieveVarieties = (cultureID: string) => {
      return useQuery<Variety[], Error>({
        queryKey: [QUERY_KEYS.VARIETIES, cultureID],
        queryFn: () => retrieveVarieties(cultureID),
        staleTime: DEFAULT_STALE_TIME.VERY_LONG,
        throwOnError: false
      })
    }

    
  export const useQueryRetrievePests = () => {
    return useQuery<Pest[], Error>({
          queryKey: [QUERY_KEYS.PESTS],
          queryFn: retrievePests,
          staleTime: DEFAULT_STALE_TIME.VERY_LONG,
          throwOnError: false
        })
  }


  export const useQueryRetrieveCultureStages = () => {
    return useQuery<CultureStage[], Error>({
          queryKey: [QUERY_KEYS.CULTURE_STAGES],
          queryFn: retrieveCultureStages,
          staleTime: DEFAULT_STALE_TIME.VERY_LONG,
          throwOnError: false
        })
  }

  export const useQueryRetrieveNutrition = () => {
    const farmID = useSessionStore().currentFarm.farmID;
    const selectedYear = useSessionStore(state => state.selectedYear);
    return useQuery<NutritionApplications, Error>({
      queryKey: [QUERY_KEYS.NUTRITION, farmID, selectedYear],
      queryFn: () => retrieveNutrition(farmID, selectedYear),

      enabled: (farmID != null) && (farmID.length > 0),
      staleTime: DEFAULT_STALE_TIME.SHORT,
      throwOnError: false
    })
}
  export const  useMutationDeleteInvoice = () => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: (invoice: Invoice) => apiDeleteInvoice(invoice),
        onSuccess: () => {
          queryClient.invalidateQueries({queryKey: [QUERY_KEYS.INVOICES]});
          queryClient.invalidateQueries({queryKey: [QUERY_KEYS.INVENTORY]});
        }});
  }

  export const  useMutationDeleteTransaction = () => {    
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (transaction: Transaction) => deleteTransaction(transaction.id),
        onSuccess: () => {
          queryClient.invalidateQueries({queryKey: [QUERY_KEYS.INVENTORY]});
        }});
  }


  export const  useMutationTransferProduct = () => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: (transferInfo: any) => transferProductToOtherFarm(transferInfo),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: [QUERY_KEYS.INVENTORY]});
            queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PRODUCT_TRANSACTIONS]});
        }});
  }

  export const  useMutationSaveProduct = () => {
    
    return useMutation({
         mutationFn: (product: Product) => createOrUpdateProduct(product),
        });
  }


  export const useQueryRetrieveSplitFertigations = () => {
    const farmID = useSessionStore().currentFarm.farmID;
    return useQuery<SplitFertigation[], Error>({
      queryKey: [QUERY_KEYS.SPLIT_FERTIGATIONS, farmID],
      queryFn: () => retrieveSplitFertigations(farmID),
      enabled: (farmID.length > 0),
      staleTime: DEFAULT_STALE_TIME.SHORT,
      throwOnError: false
    })
}

export const  useMutationSaveSplitFertigation = () => {
  const queryClient = useQueryClient();
  return useMutation({
       mutationFn: (splitFertigation: SplitFertigation) => saveSplitFertigation(splitFertigation),
       onSuccess: () => {
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.INVENTORY]});
       }});
}

export const  useMutationDeleteSplitFertigation = () => {
  const queryClient = useQueryClient();    
  return useMutation({
       mutationFn: (splitFertigation: SplitFertigation) => deleteSplitFertigation(splitFertigation),
       onSuccess: () => {
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.SPLIT_FERTIGATIONS]});   
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.INVENTORY]});   
      }});
}

export const  useMutationSaveInvoice = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (invoice: Invoice) => apiSaveInvoice(invoice),
        onSuccess: () => {
          queryClient.invalidateQueries({queryKey: [QUERY_KEYS.INVOICES]});
          queryClient.invalidateQueries({queryKey: [QUERY_KEYS.INVENTORY]});

        },
    });
  }

  export const useGetProductPackaging = () => {
    const { data: packagings } = useQueryRetrievePackaging();

    return (packagingID: string) => {
        if (packagings)
          return packagings.filter(p => p.packagingID === packagingID)[0]
        else
          return undefined
  }
  }

  export const  useMutationDeleteNutrition = () => {
    const queryClient = useQueryClient();    
    return useMutation({
         mutationFn: (nutrition: NutritionEntity) => deleteNutrition(nutrition),
         onSuccess: () => {
          queryClient.invalidateQueries({queryKey: [QUERY_KEYS.NUTRITION]});   
          queryClient.invalidateQueries({queryKey: [QUERY_KEYS.INVENTORY]});   
        }});
  }

export const  useMutationSaveNutrition = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (nutrition: NutritionEntity) => saveOneNutritionProduct(nutrition),
        onSuccess: () => {
          queryClient.invalidateQueries({queryKey: [QUERY_KEYS.NUTRITION]});
          queryClient.invalidateQueries({queryKey: [QUERY_KEYS.INVENTORY]});

        },
    });
}

export const  useMutationExecuteDayFertigation = () => {
  const queryClient = useQueryClient();
  return useMutation({
      mutationFn: (nutritions: NutritionEntity[]) => executeDayFertigation(nutritions),
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.NUTRITION]});
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.INVENTORY]});

      },
  });
}





export const useQueryRetrievePlannedSprays = () => {
  const { selectedYear } = useSessionStore();
  const farmID = useSessionStore().currentFarm.farmID;
  return useQuery<SprayCampaign[], Error>({
    queryKey: [QUERY_KEYS.SPRAY_PLANNED, farmID, selectedYear],
    queryFn: () => retrieveSprayPlannedList(farmID, selectedYear),
    enabled: (farmID.length > 0),
    staleTime: 60000,
    throwOnError: false
  })
}

export const useQueryRetrieveSpray = (treatmentID: string) => {
  const farmID = useSessionStore().currentFarm.farmID;
  return useQuery<SprayCampaign, Error>({
    queryKey: [QUERY_KEYS.SPRAY_PLANNED, QUERY_KEYS.SPRAY_EXECUTED, farmID, treatmentID],
    queryFn: () => retrieveSpray(farmID, treatmentID),
    enabled: (farmID.length > 0) && (treatmentID.length > 0),
    staleTime: DEFAULT_STALE_TIME.LONG,
    throwOnError: false
  })
}


export const useQueryRetrieveExecutedSprays = () => {
  const { selectedYear } = useSessionStore();
  const farmID = useSessionStore().currentFarm.farmID;
  return useQuery<SprayExecuted[], Error>({
    queryKey: [QUERY_KEYS.SPRAY_EXECUTED, farmID, selectedYear],
    queryFn: () => retrieveSprayExecutedList(farmID, selectedYear),
    enabled: (farmID.length > 0),
    staleTime: 60000,
    throwOnError: false
  })
}
 
export const useQueryRetrieveSprayProducts = (sprayID: string) => {
  const farmID = useSessionStore().currentFarm.farmID;
  return useQuery<SprayProduct[], Error>({
    queryKey: [QUERY_KEYS.SPRAY_PRODUCTS, farmID, sprayID],
    queryFn: () => retrieveSprayProducts(farmID, sprayID),
    enabled:  (farmID.length > 0) && (sprayID?.length > 0),
    staleTime: DEFAULT_STALE_TIME.SHORT,
    throwOnError: false
  })
}




export const useQueryRetrieveFruitSizeMeasurements = (blockID: string | undefined, varietyID: string | undefined, treeID: string | undefined, fruitID: string | undefined,
  date?: Date | undefined, year?: number | undefined) => {
  const farmID = useSessionStore().currentFarm.farmID;
  return useQuery<FruitSizeMeasurement[], Error>({
    queryKey: [QUERY_KEYS.FRUIT_SIZE_MEASUREMENTS, farmID, blockID, varietyID, treeID, fruitID, date, year],
    queryFn: () => retrieveFruitSizeMeasurements(farmID, blockID, varietyID, treeID, fruitID, date, year),
    enabled:  (farmID.length > 0) && (blockID !== undefined || varietyID !== undefined || treeID !== undefined),
    staleTime: DEFAULT_STALE_TIME.SHORT,
    throwOnError: false
  })
}




export const  useMutationSaveSprayPlanned = () => {
  const queryClient = useQueryClient();
  return useMutation({
      mutationFn: (spray: SprayCampaign) => saveOneSprayPlanned(spray),
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.SPRAY_PLANNED]});
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.SPRAY_PRODUCTS]});
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.INVENTORY]});

      },
  });
}

export const  useMutationSaveSprayExecuted = () => {
  const queryClient = useQueryClient();
  return useMutation({
      mutationFn: (spray: SprayExecuted) => saveOneSprayExecuted(spray),
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.SPRAY_EXECUTED]});
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.SPRAY_PRODUCTS]});
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.INVENTORY]});

      },
  });
}

export const  useMutationSaveMultipleSprayExecuted = () => {
  const queryClient = useQueryClient();
  return useMutation({
      mutationFn: ({spray, sprayTankCount}: {spray: SprayExecuted, sprayTankCount: number}) => saveMultipleSprayExecuted(spray, sprayTankCount), 
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.SPRAY_EXECUTED]});
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.SPRAY_PRODUCTS]});
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.INVENTORY]});

      },
  });
}




export const  useMutationDeleteSprayPlanned = () => {
  const queryClient = useQueryClient();
  return useMutation({
      mutationFn: (spray: SprayCampaign) => deleteOneSprayPlanned(spray),
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.SPRAY_PLANNED]});
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.SPRAY_PRODUCTS]});
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.INVENTORY]});
      },
  });
}

export const  useMutationSetSprayCompleted = () => {
  const queryClient = useQueryClient();
  return useMutation({
      mutationFn: (treatmentID: string) => setSprayCompleted(treatmentID),
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.SPRAY_PLANNED]});
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.SPRAY_PRODUCTS]});
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.INVENTORY]});
      },
  });
}
export const  useMutationDeleteOneSprayExecuted = () => {
  const queryClient = useQueryClient();
  return useMutation({
      mutationFn: (spray: SprayExecuted) => deleteOneSprayExecuted(spray),
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.SPRAY_EXECUTED]});
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.SPRAY_PRODUCTS]});
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.INVENTORY]});
      },
  });
}


export const  useMutationSaveFruitSizeMeasurements = () => {
  const queryClient = useQueryClient();
  return useMutation({
      mutationFn: (measurements: FruitSizeMeasurement[]) => saveFruitSizeMeasurements(measurements),
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.FRUIT_SIZE_MEASUREMENTS]});

      },
  });
}

export const  useQueryRetrieveFruitSizeMeasurementStats = (blockID: string | undefined, varietyID: string | undefined, treeID: string | undefined, fruitID: string | undefined, year?: number | undefined) => {
  const farmID = useSessionStore().currentFarm.farmID;
  return useQuery<FruitSizeMeasurementStats[], Error>({
    queryKey: [QUERY_KEYS.FRUIT_SIZE_MEASUREMENT_STATS, farmID, blockID, varietyID, treeID, fruitID,  year],
    queryFn: () => retrieveFruitSizeMeasurementStats(farmID, blockID, varietyID, treeID, fruitID, year),
    enabled:  (farmID.length > 0) && (blockID !== undefined || varietyID !== undefined || treeID !== undefined || fruitID !== undefined),
    staleTime: DEFAULT_STALE_TIME.SHORT,
    throwOnError: false
  })
}

export const useQueryPingServer = () => {
  return useQuery<string, Error>({
    queryKey: [QUERY_KEYS.PING_SERVER],
    queryFn: () => pingServer(),
    enabled: true,
    staleTime: DEFAULT_STALE_TIME.PING,
    throwOnError: false
  })
}


/************************************************************************************************************
* 
************************************************************************************************************/
// #region "Configuration"
/*--------------------------------------------------------------------------------------------------------------
 Configuration/Database related queries
*---------------------------------------------------------------------------------------------------------------*/

async function retrieveVATRates(farmID: string)  {
  return await apiService.secureGET("/conf/vatrates", {farmID: farmID});
}

async function retrieveUnits(mvuOnly: boolean  = true, concentrationOnly: boolean  = false)  {
  return await apiService.secureGET("/conf/units", {mvuOnly: mvuOnly, concentrationOnly: concentrationOnly});
}

async function retrieveUnitList(unitListType: UnitType = UnitType.Any)  {
  const mvuOnly = (unitListType === UnitType.MVU);
  const concentrationOnly = (unitListType === UnitType.Concentration);
  return await apiService.secureGET("/conf/units", {mvuOnly: mvuOnly, concentrationOnly: concentrationOnly});
}

async function retrieveIngredients()  {
return await apiService.secureGET("/conf/ingredients", {});
}

async function retrievePackagings()  {
return await apiService.secureGET("/conf/packagings", {});
}

async function retrieveFormulations () {
return await apiService.secureGET("/conf/formulations", {});
}

async function retrieveCategories () {
return await apiService.secureGET("/conf/categories", {});
}

async function retrieveUsageModes () {
return await apiService.secureGET("/conf/usagemodes", {});
}

async function retrieveCultures () {
return await apiService.secureGET("/conf/cultures", {});
}

async function retrieveCultureStages () {
return await apiService.secureGET("/conf/stages", {});
}

async function retrieveFarms () {
    return await apiService.secureGET("/user/farms", {});
}

async function retrieveFarmDetails (farmID: string) {
  return await apiService.secureGET("/user/farm", {farmID: farmID});
}


async function retrievePests () {
  return await apiService.secureGET("/conf/pests", {});
}

async function retrieveBlocksCultures (farmID: string) {
  return await apiService.secureGET("/conf/farm/blockscultures", {farmID: farmID});
}

async function retrieveBlocks (farmID: string) {
  return await apiService.secureGET("/conf/farm/blocks", {farmID: farmID});
}

async function retrieveVarieties (cultureID: string) {
  return await apiService.secureGET("/conf/varieties", {cultureID: cultureID});
}

async function retrieveTrees (farmID: string) {
  return await apiService.secureGET("/conf/farm/trees", {farmID: farmID});
}
// #endregion
// #region "Product"
/*--------------------------------------------------------------------------------------------------------------
* Product related queries
*---------------------------------------------------------------------------------------------------------------*/
async function retrieveProducts(farmID: string)  {
  return await apiService.secureGET("/products", {farmID: farmID});
}

async function createOrUpdateProduct(product: Product) {
  return await apiService.securePOST("/product/edit", product);
}
async function retrieveProductCategories (productID: string) {
  return await apiService.secureGET("/product/categories", {productID: productID});
}

async function retrieveProductIngredients (productID: string) {
  return await apiService.secureGET("/product/ingredients", {productID: productID});
}

async function retrieveProductUsages (productID: string) {
  return await apiService.secureGET("/product/usages", {productID: productID});
}

// #endregion
// #region "Invoice"
/*--------------------------------------------------------------------------------------------------------------
* Invoice related queries
*---------------------------------------------------------------------------------------------------------------*/

async function retrieveInvoices(farmID : string, selectedYear: number)  {
   return await apiService.secureGET("/invoices", {farmID: farmID, selectedYear: selectedYear})
  }

async function retrieveInvoiceProducts(farmID : string, productID: string)  {
     return await apiService.secureGET("/invoice/products", {farmID: farmID, productID: productID})
  }
    
async function apiSaveInvoice(invoice : Invoice)  {
     return await apiService.securePOST("/invoice/edit", invoice);
  }
  
async function apiDeleteInvoice(invoice : Invoice)  {
    await apiService.securePOST("/invoices/deleteInvoice", invoice);
  }

// #endregion
// #region "Inventory"
/*--------------------------------------------------------------------------------------------------------------
* Inventory related queries
*---------------------------------------------------------------------------------------------------------------*/

async function retrieveInventory(farmID: string, selectedYear: number)  {
  return await apiService.secureGET("/inventory", {farmID: farmID, fertigationOnly: false, selectedYear: selectedYear});
}

async function retrieveInventoryProduct(farmID: string, productID: string, selectedYear: number)  {
  return await apiService.secureGET("/inventory/product", {farmID: farmID, productID: productID, selectedYear: selectedYear});
}

async function retrieveProductTransactions(farmID: string, productID: string, selectedYear: number)  {
  return await apiService.secureGET("/inventory/product/transactions", {farmID: farmID, productID: productID, selectedYear: selectedYear});
}

async function retrieveTransactions(farmID: string, selectedYear: number)  {
  return await apiService.secureGET("/inventory/transactions", {farmID: farmID, selectedYear: selectedYear});
}

async function retrieveInventoryTransactionTypes()  {
  return await apiService.secureGET("/inventory/transactiontypes", {});
}

async function transferProductToOtherFarm (transferInfo: any) {
  return await apiService.securePOST("/inventory/transfer", transferInfo);
}

async function deleteTransaction (transactionID: string) {
  return await apiService.securePOST("/inventory/transaction/delete", transactionID);
}

// #endregion
// #region "Nutrition"
/*--------------------------------------------------------------------------------------------------------------
* Nutrition related queries
*---------------------------------------------------------------------------------------------------------------*/

export async function retrieveNutrition(farmID: string, selectedYear: number)  {
  return await apiService.secureGET("/nutrition/program", {farmID: farmID, selectedYear: selectedYear});
}

export async function saveOneNutritionProduct(nutrition: NutritionEntity)  {
  return await apiService.securePOST("/nutrition/program/product/edit", nutrition);
}

export async function executeDayFertigation(nutritions: NutritionEntity[])  {
  return await apiService.securePOST("/nutrition/program/execute", nutritions);
}

export async function retrieveSplitFertigations(farmID: string)  {
  return await apiService.secureGET("/nutrition/splitfertigations", {farmID: farmID});
}

export async function saveSplitFertigation(splitFertigation: SplitFertigation)  {
  await apiService.securePOST("/nutrition/splitfertigations/edit", splitFertigation);
}

export async function deleteNutrition(nutrition: NutritionEntity)  {
  await apiService.securePOST("/nutrition/program/delete", nutrition );
}

export async function deleteSplitFertigation(splitFertigation: SplitFertigation)  {
  await apiService.securePOST("/nutrition/splitfertigations/delete", splitFertigation );
}

// #endregion
// #region "Sprays"
/*--------------------------------------------------------------------------------------------------------------
* Sprays related queries
*---------------------------------------------------------------------------------------------------------------*/

async function retrieveSpray(farmID: string, treatmentID: string)  {
  return await apiService.secureGET("/spray", {farmID: farmID, treatmentID: treatmentID});
}

async function retrieveSprayPlannedList(farmID: string, selectedYear: number)  {
  return await apiService.secureGET("/sprays/planned", {farmID: farmID, selectedYear: selectedYear});
}

async function retrieveSprayExecutedList(farmID: string, selectedYear: number)  {
  return await apiService.secureGET("/sprays/executed", {farmID: farmID, selectedYear: selectedYear});
}

async function retrieveSprayProducts(farmID: string, sprayID: string)  {
  return await apiService.secureGET("/sprays/planned/products", {sprayID: sprayID, farmID: farmID});
}

async function saveOneSprayPlanned(spray: SprayCampaign)  {
  return await apiService.securePOST("/sprays/planned/edit", {sprayApplication: spray});
}

async function saveOneSprayExecuted(spray: SprayExecuted)  {
  return await apiService.securePOST("/sprays/executed/edit", {sprayApplication: spray});
}
async function saveMultipleSprayExecuted(spray: SprayExecuted, sprayTankCount: number)  {
  return await apiService.securePOST("/sprays/executed/multiple", {sprayApplication: spray, sprayTankCount: sprayTankCount});
}


async function deleteOneSprayPlanned(spray: SprayCampaign)  {
  return await apiService.securePOST("/sprays/planned/delete", {sprayApplication: spray});
}

async function setSprayCompleted(treatmentID: string)  {
  return await apiService.securePOST("/sprays/planned/complete", {treatmentID: treatmentID});
}

async function deleteOneSprayExecuted(spray: SprayExecuted)  {
  return await apiService.securePOST("/sprays/executed/delete", {sprayApplication: spray});
}

async function saveFruitSizeMeasurements(measurements: FruitSizeMeasurement[])  {
  return await apiService.securePOST("/monitoring/fruitsize/save", measurements);
}

async function retrieveFruitSizeMeasurements(farmID: string, blockID: string | undefined, 
  varietyID: string | undefined, treeID: string | undefined, fruitID: string | undefined, date?: Date | undefined, year?: number | undefined)  {
  const dateString = formatDateYYYYMMDD(date, "-" );
  return await apiService.secureGET("/monitoring/fruitsize/get", {farmID: farmID, blockID: blockID, varietyID: varietyID, treeID: treeID, fruitID: fruitID, date: dateString, year: year});
}

async function retrieveFruitSizeMeasurementStats(farmID: string, blockID: string | undefined, 
  varietyID: string | undefined, treeID: string | undefined, fruitID: string | undefined, year?: number | undefined)  {
  
  const stats = await apiService.secureGET("/monitoring/fruitsize/stats", {farmID: farmID, blockID: blockID, varietyID: varietyID, treeID: treeID, fruitID: fruitID, year: year});
  console.log( "stats", JSON.stringify(stats));
  return stats;
}


async function pingServer()  {
  return await apiService.secureGET("/ping", {});
}

